// src/components/ApplyShell.tsx: chrome and form primitives shared by the
// allocator, service provider and candidate application flows. Mirrors the
// layout of the manager flow in pages/Apply.tsx so all four look identical.
import React from 'react';
import { C } from '../lib/applications';

export const card: React.CSSProperties = {
  background: C.white, border: `1px solid ${C.border}`,
  borderRadius: '4px', boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
};
export const inputStyle: React.CSSProperties = {
  width: '100%', background: C.white, border: `1px solid ${C.border}`,
  borderRadius: '3px', color: C.text, fontSize: '13px', padding: '9px 11px',
  outline: 'none', fontFamily: 'system-ui', boxSizing: 'border-box',
};
export const labelStyle: React.CSSProperties = {
  fontSize: '10px', fontWeight: 700, color: C.muted, letterSpacing: '0.6px',
  textTransform: 'uppercase', display: 'block', marginBottom: '5px',
};
export const btnPrimary: React.CSSProperties = {
  background: C.navy, color: C.white, border: 'none', borderRadius: '3px',
  padding: '11px 24px', fontSize: '13px', fontWeight: 600, cursor: 'pointer', fontFamily: 'system-ui',
};
export const btnGhost: React.CSSProperties = {
  background: C.white, color: C.textSec, border: `1px solid ${C.border}`,
  borderRadius: '3px', padding: '11px 20px', fontSize: '13px', fontWeight: 600,
  cursor: 'pointer', fontFamily: 'system-ui',
};
export const stepHeading: React.CSSProperties = {
  fontFamily: 'Georgia, "Times New Roman", serif', fontSize: '20px',
  color: C.text, fontWeight: 400, margin: '0 0 18px',
};

export const Field = ({ label, hint, children }: {
  label: string; hint?: string; children: React.ReactNode;
}) => (
  <div style={{ marginBottom: '14px' }}>
    <label style={labelStyle}>{label}</label>
    {hint && <div style={{ fontSize: '11.5px', color: C.mutedLt, margin: '-2px 0 6px', lineHeight: 1.5 }}>{hint}</div>}
    {children}
  </div>
);

export const Row = ({ k, v }: { k: string; v: React.ReactNode }) => (
  <div style={{ display: 'flex', gap: '12px', padding: '7px 0', borderBottom: `1px solid ${C.borderLt}`, fontSize: '12.5px' }}>
    <div style={{ width: '190px', flexShrink: 0, color: C.muted }}>{k}</div>
    <div style={{ color: C.text, fontWeight: 500, wordBreak: 'break-word' }}>{v || '-'}</div>
  </div>
);

export const Section = ({ title, children }: { title: string; children: React.ReactNode }) => (
  <div style={{ marginBottom: '18px' }}>
    <div style={{ fontSize: '10px', fontWeight: 700, color: C.muted, letterSpacing: '0.6px', textTransform: 'uppercase', marginBottom: '6px' }}>{title}</div>
    {children}
  </div>
);

export const ErrorBanner = ({ text }: { text: string }) => (
  <div style={{ background: '#FEF2F2', border: '1px solid #FECACA', borderRadius: '3px', padding: '10px 13px', marginBottom: '16px', fontSize: '12.5px', color: C.danger }}>{text}</div>
);

export const InfoBox = ({ children }: { children: React.ReactNode }) => (
  <div style={{
    background: 'rgba(29,111,164,0.06)', border: `1px solid rgba(29,111,164,0.25)`,
    borderRadius: '3px', padding: '12px 15px', margin: '4px 0 18px',
    fontSize: '12.5px', color: C.textSec, lineHeight: 1.65,
  }}>{children}</div>
);

/** Multi-select rendered as toggle pills, matching ChipSelect elsewhere. */
export function CheckboxGroup({ options, selected, onToggle, columns }: {
  options: { value: string; label: string }[];
  selected: string[];
  onToggle: (value: string) => void;
  columns?: number;
}) {
  return (
    <div style={columns
      ? { display: 'grid', gridTemplateColumns: `repeat(${columns}, minmax(0, 1fr))`, gap: '6px' }
      : { display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
      {options.map(o => {
        const on = selected.includes(o.value);
        return (
          <button key={o.value} type="button" onClick={() => onToggle(o.value)} style={{
            padding: '7px 12px', borderRadius: '3px', fontSize: '12px', cursor: 'pointer',
            fontFamily: 'system-ui', fontWeight: 500, textAlign: columns ? 'left' : 'center',
            border: `1px solid ${on ? C.navy : C.border}`,
            background: on ? C.navy : C.white, color: on ? C.white : C.textSec,
          }}>{on ? '✓ ' : ''}{o.label}</button>
        );
      })}
    </div>
  );
}

/** Yes / No pair, used where a plain checkbox would read ambiguously. */
export function YesNo({ value, onChange, yesLabel = 'Yes', noLabel = 'No' }: {
  value: boolean; onChange: (v: boolean) => void; yesLabel?: string; noLabel?: string;
}) {
  const btn = (on: boolean): React.CSSProperties => ({
    padding: '8px 20px', borderRadius: '3px', fontSize: '12.5px', cursor: 'pointer',
    fontFamily: 'system-ui', fontWeight: 600,
    border: `1px solid ${on ? C.navy : C.border}`,
    background: on ? C.navy : C.white, color: on ? C.white : C.textSec,
  });
  return (
    <div style={{ display: 'flex', gap: '8px' }}>
      <button type="button" onClick={() => onChange(true)} style={btn(value)}>{yesLabel}</button>
      <button type="button" onClick={() => onChange(false)} style={btn(!value)}>{noLabel}</button>
    </div>
  );
}

/** Free-text tags, capped so a profile cannot be stuffed with keywords. */
export function TagInput({ tags, onChange, max, placeholder }: {
  tags: string[]; onChange: (t: string[]) => void; max: number; placeholder?: string;
}) {
  const ref = React.useRef<HTMLInputElement>(null);
  const add = () => {
    const v = ref.current?.value.trim();
    if (v && !tags.includes(v) && tags.length < max) onChange([...tags, v]);
    if (ref.current) ref.current.value = '';
  };
  return (
    <div>
      {tags.length > 0 && (
        <div style={{ display: 'flex', gap: '6px', marginBottom: '8px', flexWrap: 'wrap' }}>
          {tags.map(t => (
            <span key={t} style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', background: C.surface, border: `1px solid ${C.border}`, color: C.textSec, fontSize: '12px', fontWeight: 500, padding: '4px 9px', borderRadius: '3px' }}>
              {t}
              <button type="button" onClick={() => onChange(tags.filter(x => x !== t))}
                style={{ background: 'none', border: 'none', color: C.mutedLt, cursor: 'pointer', fontSize: '14px', lineHeight: 1, padding: 0 }}>×</button>
            </span>
          ))}
        </div>
      )}
      <div style={{ display: 'flex', gap: '6px' }}>
        <input ref={ref} style={inputStyle} disabled={tags.length >= max}
          placeholder={tags.length >= max ? `Maximum ${max} reached` : (placeholder || 'Type and press Enter')}
          onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); add(); } }} />
        <button type="button" onClick={add} disabled={tags.length >= max}
          style={{ ...btnGhost, flexShrink: 0, opacity: tags.length >= max ? 0.5 : 1 }}>Add</button>
      </div>
      <div style={{ fontSize: '11px', color: C.mutedLt, marginTop: '5px' }}>{tags.length} of {max}</div>
    </div>
  );
}

/** Numbered progress rail across the top of every apply flow. */
export function ProgressRail({ steps, step }: { steps: string[]; step: number }) {
  return (
    <div style={{ display: 'flex', alignItems: 'flex-start', marginBottom: '26px' }}>
      {steps.map((label, i) => {
        const n = i + 1;
        const done = n < step;
        const current = n === step;
        return (
          <React.Fragment key={label}>
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', flexShrink: 0, width: '92px' }}>
              <div style={{
                width: '28px', height: '28px', borderRadius: '50%',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontSize: '12px', fontWeight: 700, marginBottom: '6px',
                background: done ? C.success : current ? C.gold : C.white,
                color: done || current ? C.white : C.mutedLt,
                border: `1px solid ${done ? C.success : current ? C.gold : C.border}`,
              }}>{done ? '✓' : n}</div>
              <div style={{
                fontSize: '9.5px', textAlign: 'center', lineHeight: 1.35,
                fontWeight: current ? 700 : 500,
                color: current ? C.navy : done ? C.textSec : C.mutedLt,
              }}>{label}</div>
            </div>
            {i < steps.length - 1 && (
              <div style={{ flex: 1, height: '1px', background: n < step ? C.success : C.border, marginTop: '14px' }} />
            )}
          </React.Fragment>
        );
      })}
    </div>
  );
}

export function AltvenoMark({ size = 26 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 28 28" fill="none">
      <path d="M 4 14 A 10 10 0 0 1 24 14" stroke="#5dc9a8" strokeWidth="2.2" strokeLinecap="round" />
      <path d="M 6 14 A 8 8 0 0 0 22 14" stroke={C.gold} strokeWidth="2.2" strokeLinecap="round" />
      <circle cx="14" cy="14" r="2.2" fill={C.gold} />
    </svg>
  );
}

/** Navy header strip, with an optional gold badge such as "ALWAYS FREE". */
export function ApplyShell({ title, subtitle, badge, children }: {
  title: string; subtitle: string; badge?: string; children: React.ReactNode;
}) {
  return (
    <div style={{ minHeight: '100vh', background: C.surface, fontFamily: 'system-ui, -apple-system, sans-serif' }}>
      <div style={{ background: C.navy, padding: '22px 32px 26px' }}>
        <div style={{ maxWidth: '720px', margin: '0 auto' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '9px', marginBottom: '18px' }}>
            <AltvenoMark />
            <span style={{ fontFamily: 'Georgia, "Times New Roman", serif', fontSize: '18px', color: C.white }}>Altveno</span>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap', marginBottom: '6px' }}>
            <h1 style={{ fontFamily: 'Georgia, "Times New Roman", serif', fontSize: '26px', color: C.white, fontWeight: 400, margin: 0, letterSpacing: '-0.3px' }}>
              {title}
            </h1>
            {badge && (
              <span style={{
                background: 'rgba(201,168,76,0.15)', color: C.gold,
                border: `1px solid rgba(201,168,76,0.4)`, borderRadius: '2px',
                fontSize: '9.5px', fontWeight: 700, letterSpacing: '1px',
                textTransform: 'uppercase', padding: '4px 10px', whiteSpace: 'nowrap',
              }}>{badge}</span>
            )}
          </div>
          <p style={{ fontSize: '13.5px', color: 'rgba(255,255,255,0.5)', margin: 0, lineHeight: 1.6 }}>{subtitle}</p>
        </div>
      </div>
      {children}
    </div>
  );
}

/** Shared post-submit screen. */
export function SubmittedScreen({ email, timeline, onContinue }: {
  email: string; timeline: string; onContinue: () => void;
}) {
  return (
    <div style={{ ...card, padding: '44px 40px', maxWidth: '560px', margin: '48px auto', textAlign: 'center' }}>
      <div style={{
        width: '46px', height: '46px', borderRadius: '50%', background: '#F0FDF4',
        border: '1px solid #BBF7D0', display: 'flex', alignItems: 'center',
        justifyContent: 'center', margin: '0 auto 18px', color: C.success, fontSize: '20px',
      }}>✓</div>
      <h1 style={{ fontFamily: 'Georgia, serif', fontSize: '24px', color: C.text, fontWeight: 400, margin: '0 0 12px' }}>
        Application submitted successfully
      </h1>
      <p style={{ fontSize: '13.5px', color: C.textSec, lineHeight: 1.75, margin: '0 0 10px' }}>
        We have sent a confirmation email to <strong>{email}</strong>.
      </p>
      <p style={{ fontSize: '13.5px', color: C.muted, lineHeight: 1.75, margin: '0 0 26px' }}>{timeline}</p>
      <button style={btnPrimary} onClick={onContinue}>Go to application portal</button>
    </div>
  );
}
